import {
    DataGrid,
    GridColDef,
    GridRowId,
    gridFilteredSortedRowIdsSelector,
    useGridApiRef,
} from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import { gridClasses } from '@mui/x-data-grid';
import moment from 'moment';
import { Box, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { openReportAPIRefSliceAction } from '../../../../../app/Slices/ReportAPIRefSlice';
import UserRole from '../../../../../shared/AuthRoles';
import { CustomChip } from '../../../../../layout/styledcomponents/CustomChip';
import { Link } from 'react-router-dom';
import CustomNoRowsOverlay from '../../../../shared/NoRowsOverlayCustom';

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
    '& .MuiTablePagination-toolbar': {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignContent: 'center',
    },
    '& .MuiTablePagination-displayedRows ': {
        paddingTop: '14px',
    },
    '& .MuiTablePagination-selectLabel': {
        paddingTop: '14px',
    },
    '& .MuiTablePagination-select': {
        paddingTop: '4px',
    },

    '& .MuiDataGrid-columnHeader': {
        fontSize: '12px',
        fontWeight: 900,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.midGrey.main}`,
        paddingLeft: '0px',
    },
    [`& .${gridClasses.row}`]: {
        '&:hover, &.Mui-hovered': {
            backgroundColor: 'none',
            color: theme.palette.green.main,

            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
        '&.Mui-selected': {
            backgroundColor: theme.palette.lightGrey.main,

            color: theme.palette.green.main,
            '&:hover, &.Mui-hovered': {
                backgroundColor: 'none',
                color: theme.palette.green.main,

                '@media (hover: none)': {
                    backgroundColor: 'none',
                },
            },
        },
    },
}));

export default function CompletedListReport(params: any) {
    const loggedInUser = UserRole();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const apiRef = useGridApiRef();
    const dispatch = useDispatch();
    const urlRef = window.location.href;

    const [data, setData] = useState<GridRowId[]>();

    useEffect(() => {
        const csv = apiRef?.current.exportDataAsCsv;
        const print = apiRef?.current.exportDataAsPrint;
        dispatch(
            openReportAPIRefSliceAction({
                exportInfo: {
                    csvExport: csv,
                    printExport: print,
                    search: apiRef?.current.setQuickFilterValues,
                },
            }),
        );
    }, [apiRef, dispatch]);

    const renderLinkCell = (params: any) => (
        <Link
            to={`/reservations/${params.row.moduleName
                .replace(/ /g, '')
                .toLowerCase()}/${params.row.id}`}
            state={{
                name: 'viewReservation',
                component: 'dashboard',
                url: `dashboard/completed/${urlRef.split('/')[5]}`,
                params: params.row,
                index: data?.findIndex((value) => value === params.id),
                reservations: data,
            }}
            style={{
                textDecoration: 'none',
                color: 'inherit',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
            }}
        >
            {params.value}
        </Link>
    );

    const columns: GridColDef[] = [
        {
            field: 'moduleName',
            headerName: 'Experience',
            flex: 1,
            minWidth: isIpad ? 200 : 200,
            type: 'string',
            sortable: true,
            renderCell: renderLinkCell,
        },
        {
            field: 'ownerName',
            headerName: 'Host',
            description: 'Owner of experience',
            width: isMobile ? 80 : isIpad ? 80 : 150,
            type: 'string',
            sortable: true,
            renderCell: (params) =>
                renderLinkCell({
                    ...params,
                    value:
                        params?.value?.charAt(0).toUpperCase() +
                        params?.value?.slice(1),
                }),
        },
        {
            field: 'moduleType',
            headerName: 'Type',
            width: isMobile ? 80 : isIpad ? 80 : 80,
            type: 'string',
            sortable: true,
            renderCell: (params) =>
                renderLinkCell({
                    ...params,
                    value:
                        params?.value?.charAt(0).toUpperCase() +
                        params?.value?.slice(1),
                }),
        },
        {
            field: 'id',
            headerName: 'Reservation Ref',
            width: isMobile ? 80 : isIpad ? 80 : 80,
            type: '',
            sortable: true,
            renderCell: (params) =>
                renderLinkCell({
                    ...params,
                    value: `#${params.value}`,
                }),
        },
        {
            field: 'bookerCompany',
            headerName: 'Company',
            description: 'Companies who have made the reservation',
            width: isMobile ? 80 : isIpad ? 80 : 150,
            type: 'string',
            sortable: true,
            renderCell: (params) =>
                renderLinkCell({
                    ...params,
                    value:
                        params?.value?.charAt(0).toUpperCase() +
                        params?.value?.slice(1),
                }),
        },
        {
            field: 'bookedBy',
            headerName: 'Reserved By',
            description: 'Person who made the reservation',
            width: isMobile ? 80 : isIpad ? 80 : 150,
            type: 'string',
            sortable: true,
            renderCell: (params) =>
                renderLinkCell({
                    ...params,
                    value:
                        params?.value?.charAt(0).toUpperCase() +
                        params?.value?.slice(1),
                }),
        },
        {
            field: 'bookedFor',
            headerName: 'Guest',
            description: 'Guest attending',
            width: isMobile ? 80 : isIpad ? 80 : 150,
            type: 'string',
            sortable: true,
            renderCell: (params) =>
                renderLinkCell({
                    ...params,
                    value:
                        params?.value?.charAt(0).toUpperCase() +
                        params?.value?.slice(1),
                }),
        },
        {
            field: 'startDate',
            headerName: 'Reservation From',
            type: 'date',
            width: isMobile ? 200 : isIpad ? 80 : 100,
            sortable: true,
            valueGetter: (params) => new Date(params.value),
            renderCell: (params) =>
                renderLinkCell({
                    ...params,
                    value: moment(params.value).format('DD/MM/YYYY'),
                }),
        },
        {
            field: 'endDate',
            headerName: 'Reservation To',
            type: 'date',
            width: isMobile ? 200 : isIpad ? 80 : 100,
            sortable: true,
            valueGetter: (params) => new Date(params.value),
            renderCell: (params) =>
                renderLinkCell({
                    ...params,
                    value: moment(params.value).format('DD/MM/YYYY'),
                }),
        },
        {
            field: 'status',
            headerName: 'Status',
            width: isMobile ? 150 : isIpad ? 150 : 150,
            type: 'string',
            sortable: true,
            renderCell: (params) =>
                renderLinkCell({
                    ...params,
                    value: (
                        <CustomChip
                            label={
                                params?.value.charAt(0).toUpperCase() +
                                params?.value.slice(1)
                            }
                            color={
                                params.value === 'completed'
                                    ? 'green'
                                    : params.value === 'cancelled'
                                      ? 'red'
                                      : params.value === 'approved'
                                        ? 'yellow'
                                        : params.value === 'declined'
                                          ? 'gray'
                                          : 'blue'
                            }
                            variant='outlined'
                        />
                    ),
                }),
        },
    ];

    const handleStateChange = () => {
        setData(gridFilteredSortedRowIdsSelector(apiRef));
    };

    return (
        <Box
            sx={{
                height: 'auto',
                width: isMobile ? '90vw' : '100%',
                paddingTop: '50px',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    overflowX: 'auto',
                }}
            >
                <StripedDataGrid
                    getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0
                            ? 'even'
                            : 'odd'
                    }
                    onStateChange={handleStateChange}
                    sx={{
                        boxShadow: 0,
                        border: 0,

                        '& .MuiDataGrid-cell': {
                            padding: '0',
                        },
                    }}
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                bookerCompany:
                                    loggedInUser?.role === 'owner_admin' ||
                                    loggedInUser?.role === 'owner_user' ||
                                    loggedInUser?.role === 'admin',
                                bookedBy:
                                    loggedInUser?.role === 'guest_admin' ||
                                    loggedInUser?.role ===
                                        'guest_department_admin',
                                owner: loggedInUser?.role === 'admin',
                            },
                        },
                    }}
                    apiRef={apiRef}
                    disableColumnFilter
                    disableColumnSelector
                    disableDensitySelector
                    rows={params?.data !== undefined ? params?.data : []}
                    columns={columns}
                    slots={{
                        noRowsOverlay: CustomNoRowsOverlay,
                    }}
                />
            </Box>
        </Box>
    );
}
